export function usePaginationPages(
  currentPageNumber: Ref<number>,
  totalPagesNumber: Ref<number>,
  maxVisiblePageButtons: Ref<number>,
  useEllipsis: Ref<boolean>,
  lockFirstAndLastPages: Ref<boolean>
) {
  return computed<PageButton[]>(() => {
    const pages: PageButton[] = [];
    const total = totalPagesNumber.value;
    const current = currentPageNumber.value;
    const limit = maxVisiblePageButtons.value;

    function addPage(number: number) {
      pages.push({ number, label: number.toString(), key: `page-${number}`, disabled: false });
    }

    function addEllipsis(key: string) {
      pages.push({ number: -1, label: '…', key, disabled: true });
    }

    function buildSinglePage() {
      addPage(1);
    }

    function buildNearStart() {
      for (let page = 2; page <= Math.min(limit - 1, total - 1); page++) {
        addPage(page);
      }
      if (total > limit) addEllipsis('ellipsis-end');
    }

    function buildNearEnd() {
      if (total > limit) addEllipsis('ellipsis-start');
      for (let page = total - (limit - 2); page <= total - 1; page++) {
        addPage(page);
      }
    }

    function buildMiddle() {
      addEllipsis('ellipsis-start');
      addPage(current);
      addEllipsis('ellipsis-end');
    }

    function buildWithEllipsis() {
      addPage(1);
      const beginningThreshold = Math.ceil(limit / 2);
      const endThreshold = total - Math.floor(limit / 2);

      if (current <= beginningThreshold) {
        buildNearStart();
      } else if (current >= endThreshold) {
        buildNearEnd();
      } else {
        buildMiddle();
      }

      addPage(total);
    }

    function buildWithoutEllipsis() {
      const halfWindow = Math.floor(limit / 2);
      let start = Math.max(1, current - halfWindow);
      let end = start + limit - 1;

      if (end > total) {
        end = total;
        start = Math.max(1, end - limit + 1);
      }

      for (let page = start; page <= end; page++) {
        addPage(page);
      }
    }

    function buildLockedFirstAndLast() {
      addPage(1);

      const innerLimit = limit - 2;
      let start = Math.max(2, current - Math.floor(innerLimit / 2));
      let end = start + innerLimit - 1;

      if (end >= total) {
        end = total - 1;
        start = Math.max(2, end - innerLimit + 1);
      }

      for (let page = start; page <= end; page++) {
        addPage(page);
      }

      if (total > 1) {
        addPage(total);
      }
    }

    if (total === 1) {
      buildSinglePage();
    } else if (useEllipsis.value) {
      buildWithEllipsis();
    } else if (lockFirstAndLastPages.value) {
      buildLockedFirstAndLast();
    } else {
      buildWithoutEllipsis();
    }

    return pages;
  });
}
